/**
 * offlineBookingHandoff — builds the `localStorage.selectedFlight` payload
 * for an offline-block deal and navigates to `/booking?...`.
 *
 * Mirrors the shape produced by `format_offline_flight_for_display()` in
 * `backend/routes/flight_search_routes.py` AND pre-builds the Tripjack-shaped
 * `segments[]` that `<SegmentCard>` expects on `BookingPage.jsx`, so the page
 * renders a complete Flight Details card for both one-way and round-trip
 * offline deals.
 *
 * PNR is intentionally NOT included — the booking-creation backend looks up
 * the PNR by (flight_id, date) at book time.
 */

/**
 * Build a Tripjack-shaped segment for an offline-flight leg.
 * BookingPage's SegmentCard reads `seg.da`, `seg.aa`, `seg.fD`, `seg.dt`,
 * `seg.at`, `seg.duration` — we synthesise those here from the public deal
 * payload + the selected inventory row.
 *
 * For the RETURN leg of a paired RT cycle we read return-specific timing
 * fields (`return_departure_time`, `return_arrival_time`, `return_duration`,
 * `return_flight_number`) directly off the cycle row. They fall back to the
 * parent flight times when the cycle hasn't been customised.
 */
const buildSyntheticSegment = (deal, cycleRow, isReturnLeg = false) => {
  const from = isReturnLeg ? deal.route?.to_airport : deal.route?.from_airport;
  const to = isReturnLeg ? deal.route?.from_airport : deal.route?.to_airport;
  const fromCity = isReturnLeg ? deal.route?.to_city : deal.route?.from_city;
  const toCity = isReturnLeg ? deal.route?.from_city : deal.route?.to_city;

  const legDate = isReturnLeg ? cycleRow.return_date : cycleRow.date;
  const depTime = isReturnLeg
    ? (cycleRow.return_departure_time || deal.departure_time)
    : deal.departure_time;
  const arrTime = isReturnLeg
    ? (cycleRow.return_arrival_time || deal.arrival_time)
    : deal.arrival_time;
  const duration = isReturnLeg
    ? (cycleRow.return_duration || deal.duration)
    : deal.duration;
  const flightNumber = isReturnLeg
    ? (cycleRow.return_flight_number || deal.flight_number)
    : deal.flight_number;

  return {
    id: `${from}_${to}_${legDate}_${depTime || ''}`,
    da: {
      code: from, city: fromCity || from, name: fromCity || from,
      country: null, terminal: null,
    },
    aa: {
      code: to, city: toCity || to, name: toCity || to,
      country: null, terminal: null,
    },
    fD: {
      aI: { code: deal.airline_code, name: deal.airline_name },
      fN: flightNumber,
      eT: null,
    },
    dt: depTime ? `${legDate}T${depTime}:00` : null,
    at: arrTime ? `${legDate}T${arrTime}:00` : null,
    duration: duration || null,
    cT: null,
  };
};

const buildLegPayload = (deal, cycleRow, isReturnLeg = false) => {
  const from = isReturnLeg ? deal.route?.to_airport : deal.route?.from_airport;
  const to = isReturnLeg ? deal.route?.from_airport : deal.route?.to_airport;
  const fromCity = isReturnLeg ? deal.route?.to_city : deal.route?.from_city;
  const toCity = isReturnLeg ? deal.route?.from_city : deal.route?.to_city;

  const legDate = isReturnLeg ? cycleRow.return_date : cycleRow.date;
  const depTime = isReturnLeg
    ? (cycleRow.return_departure_time || deal.departure_time)
    : deal.departure_time;
  const arrTime = isReturnLeg
    ? (cycleRow.return_arrival_time || deal.arrival_time)
    : deal.arrival_time;
  const duration = isReturnLeg
    ? (cycleRow.return_duration || deal.duration)
    : deal.duration;
  const flightNumber = isReturnLeg
    ? (cycleRow.return_flight_number || deal.flight_number)
    : deal.flight_number;

  return {
    id: deal.id,
    source: 'offline',
    priceId: `offline_${deal.id}_${legDate}`,

    // Airline
    airlineCode: deal.airline_code,
    airline: deal.airline_name,
    flightNumber,

    // Route — swapped for return leg
    from, to, fromCity, toCity,

    // Type
    ticketType: deal.ticket_type,
    flightType: deal.flight_type,
    stops:
      deal.flight_type === 'direct'
        ? 'Non Stop'
        : `${(deal.segments?.length || 2) - 1} Stop`,

    // Timing — derived from the cycle row for the return leg
    departureTime: depTime,
    arrivalTime: arrTime,
    duration,

    // Pre-built Tripjack-shaped segment so <SegmentCard> renders
    segments: [buildSyntheticSegment(deal, cycleRow, isReturnLeg)],

    // Amenities
    cabinBaggage: deal.cabin_baggage,
    baggage: deal.checkin_baggage,
    mealIncluded: deal.meal_included,
    classOfBooking: deal.class_type,
    fareType: deal.fare_type,
    refundable: deal.refundable,
    refundAmount: deal.refund_amount,

    // Pricing
    fares: {
      adult: deal.lowest_fare?.adult,
      child: deal.lowest_fare?.child,
      infant: deal.lowest_fare?.infant,
    },
    baseFare: deal.lowest_fare?.adult,
    price: deal.lowest_fare?.adult,

    // Date — also stash on `flightDate` so enrichOfflineFlight() in
    // BookingPage can read it without falling back to undefined.
    flightDate: legDate,
    date: legDate,
    departureDate: legDate,
    seatsLeft: cycleRow.available_seats,
    inventoryStatus: cycleRow.status || 'active',
    bookingChannel: 'special_deals',

    // Occasion (informational — useful for booking-page promo banners later)
    occasionLabel: cycleRow.occasion_label_name || null,
    occasionColor: cycleRow.occasion_color || null,
  };
};

/**
 * Persist booking intent + navigate to /booking.
 *
 * Single-cycle handoff: for one-way the cycle row supplies `date`. For RT
 * the SAME cycle row supplies both `date` (onward) AND `return_date`
 * (return) — strict pair model.
 *
 * @param {Object}   args
 * @param {Object}   args.deal             — full deal payload (with upcoming_dates if RT)
 * @param {Object}   args.onwardDateRow    — selected cycle row (RT) or single-date row (OW)
 * @param {Object}   [args.returnDateRow]  — DEPRECATED. Ignored if the cycle has its own return_date.
 * @param {number}   args.adults
 * @param {number}   args.children
 * @param {number}   args.infants
 * @param {Function} args.navigate         — react-router navigate()
 */
export const handoffOfflineDealBooking = ({
  deal,
  onwardDateRow,
  returnDateRow,
  adults = 1,
  children = 0,
  infants = 0,
  navigate,
}) => {
  if (!deal || !onwardDateRow || !navigate) return;

  const onwardLeg = buildLegPayload(deal, onwardDateRow, false);

  let selectedFlight;
  let qs;

  // Strict RT pairing: the cycle row must carry `return_date` itself.
  // Legacy `returnDateRow` arg is supported as a fallback only for
  // historical callers — new code should pass it via the cycle row.
  const cycleForReturn = onwardDateRow.return_date
    ? onwardDateRow
    : (returnDateRow ? { ...onwardDateRow, return_date: returnDateRow.date } : null);

  if (deal.ticket_type === 'roundtrip' && cycleForReturn?.return_date) {
    const returnLeg = buildLegPayload(deal, cycleForReturn, true);
    selectedFlight = {
      type: 'roundtrip',
      source: 'offline',
      onward: onwardLeg,
      return: returnLeg,
      flightDate: onwardDateRow.date,
      totalPrice:
        ((deal.lowest_fare?.adult || 0) * (adults + children) +
          (deal.lowest_fare?.infant || 0) * infants) * 2,
      route: {
        from: deal.route?.from_airport,
        to: deal.route?.to_airport,
        departureDate: onwardDateRow.date,
        returnDate: cycleForReturn.return_date,
      },
      passengers: { adults, children, infants },
      travelClass: deal.class_type || 'Economy',
      occasionLabel: onwardDateRow.occasion_label_name || null,
      occasionColor: onwardDateRow.occasion_color || null,
      bookingChannel: 'special_deals',
    };
    qs =
      `onwardPriceId=${onwardLeg.priceId}` +
      `&returnPriceId=${returnLeg.priceId}` +
      `&type=roundtrip&adults=${adults}&children=${children}&infants=${infants}`;
  } else {
    selectedFlight = onwardLeg;
    qs =
      `priceId=${onwardLeg.priceId}&type=oneway` +
      `&adults=${adults}&children=${children}&infants=${infants}`;
  }

  try {
    localStorage.setItem('selectedFlight', JSON.stringify(selectedFlight));
    localStorage.setItem('searchTimestamp', Date.now().toString());
    sessionStorage.removeItem('acf_pending_deal_intent');
  } catch {
    /* ignore quota errors */
  }
  navigate(`/booking?${qs}`);
};
